import { useEffect, useMemo, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import Calendar from '@/Components/Modals/FilterPanel/FilterPanelCalendar';
import { Button } from '@/Components/UI/Button/Button';
import TextInput from '@/Components/UI/Form/TextInput/TextInput';
import Icon from '@/Components/UI/Icon/Icon';
import { analyzeFilterRules, COMPONENT_RULES } from '@/Utils/analyzeFilterRules';
import { MODAL_FILTER_PANEL } from '@/Utils/constants';
import getCurrentAppliedFilters from '@/Utils/getCurrentAppliedFilters';
import { getFilterType } from '@/Utils/getFilterType';
import parseArrayToMatrix from '@/Utils/parseArrayToMatrix';
import { datasetSliceName, spreadsheetSliceName } from '@/redux/constants';
import { showModal } from '@/redux/reducers/modalsSlice';
import {
  setIsCustomColumnHeaderContextMenuOpen,
  setIsPreventFiltersCall,
  setReduxFilterModelCnf,
} from '@/redux/reducers/spreadsheetSlice';

import Tooltip from '@/Components/UI/Tooltip/Tooltip';
import { useShowPreviewModeUpsellModal } from '@/hooks/useShowPreviewModeUpsellModal';
import { addMixpanelEvent } from '@/redux/reducers/sharedSlice';
import { OptionList } from './UI/OptionList';
import QuickFiltersSelect from './UI/QuickFilterSelect';
import { UnableFilters } from './UnableFilters/UnableFilters';
import { generateOperatorsOptions } from './quickFIltersHelper';

const booleanOptions = [
  { value: 'true', label: 'TRUE' },
  { value: 'false', label: 'FALSE' },
];

export const QuickFilters = () => {
  const dispatch = useDispatch();
  const showPreviewModeUpsellModal = useShowPreviewModeUpsellModal();
  const { isPreviewMode } = useSelector((state) => state[datasetSliceName]);
  const [componentMapRule, setComponentMapRule] = useState(null);
  const [searchQFValueQuery, setSearchQFValueQuery] = useState('');

  const [filterOperatorType, setFilterOperatorType] = useState('');

  const [filterTo, setFilterTo] = useState('');
  const [filterFrom, setFilterFrom] = useState('');
  const [filterSingle, setFilterSingle] = useState('');
  const [filterMultiple, setFilterMultiple] = useState([]);

  const { customContextMenuColumn, clientState } = useSelector(
    (state) => state[spreadsheetSliceName]
  );

  const { colDef } = customContextMenuColumn || {};
  const { colId, fieldType } = colDef || {};
  const { filterModel } = clientState || {};

  const isInputWithSuggestions = filterOperatorType.includes('Any') && colId !== '*';
  const isInRangeFilter = filterOperatorType === 'inRange';
  const isBlankConditions =
    filterOperatorType &&
    (filterOperatorType.includes('Blank') || filterOperatorType.includes('ternal'));
  const isFilterEmpty = filterOperatorType === 'none';
  const getIsApplyDisabled = () => {
    if (fieldType === 'Boolean' && !filterSingle.length && !isFilterEmpty) return true;
    if (
      fieldType !== 'Boolean' &&
      fieldType !== 'DateTime' &&
      fieldType !== 'DateTime64' &&
      fieldType !== 'metadata_tag' &&
      !isInRangeFilter &&
      !isBlankConditions &&
      isInputWithSuggestions &&
      !filterMultiple.length &&
      !isFilterEmpty
    ) {
      return true;
    }
    if (
      fieldType !== 'Boolean' &&
      fieldType !== 'DateTime' &&
      fieldType !== 'DateTime64' &&
      fieldType !== 'metadata_tag' &&
      !isInRangeFilter &&
      !isBlankConditions &&
      !isInputWithSuggestions &&
      !filterSingle.length &&
      !isFilterEmpty
    ) {
      return true;
    }
    if (
      isInRangeFilter &&
      getFilterType(fieldType) === 'date' &&
      (!filterTo.length || !filterFrom.length) &&
      !isFilterEmpty
    ) {
      return true;
    }

    if (
      !isInRangeFilter &&
      !isBlankConditions &&
      getFilterType(fieldType) === 'date' &&
      !filterFrom.length &&
      !isFilterEmpty
    ) {
      return true;
    }
    return false;
  };

  const operatorOptions = useMemo(() => {
    const options = generateOperatorsOptions(colId, fieldType);
    options.unshift({ label: 'None', value: 'none' });
    return options;
  }, [colId, fieldType]);

  const handleDateChange = (type, modelValue) => {
    type === 'filter' ? setFilterFrom(modelValue) : setFilterTo(modelValue);
  };

  function applyFilterModelCnfFromQuickFilters(qfFilterModel) {
    if (!filterModel?._cnf_ || !filterModel?._cnf_.length)
      return dispatch(setReduxFilterModelCnf(parseArrayToMatrix([qfFilterModel])));

    const existingIndex = filterModel._cnf_.findIndex((innerArray) => {
      return innerArray.length === 1 && innerArray[0].colId === qfFilterModel.colId;
    });

    if (existingIndex !== -1) {
      const updatedCnf = filterModel._cnf_.reduce((acc, innerArray, index) => {
        if (index === existingIndex) {
          if (!isFilterEmpty) {
            acc.push([qfFilterModel]);
          }
        } else {
          acc.push(innerArray);
        }
        return acc;
      }, []);

      dispatch(setReduxFilterModelCnf(updatedCnf));
    } else {
      dispatch(setReduxFilterModelCnf([...filterModel._cnf_, [qfFilterModel]]));
    }
  }
  const handleAdvanced = () => {
    dispatch(setIsPreventFiltersCall(true));
    handleApply();
    dispatch(showModal({ name: MODAL_FILTER_PANEL }));
    dispatch(setIsCustomColumnHeaderContextMenuOpen(false));
  };

  const handleCancel = () => {
    dispatch(setIsCustomColumnHeaderContextMenuOpen(false));
  };

  const handleApply = () => {
    const quickFilterModel = {
      colId: colId,
      isChecked: false,
      isCaseSensitive: false,
      filterType: getFilterType(fieldType),
      ...(!isBlankConditions
        ? {
            filter: isInputWithSuggestions
              ? filterMultiple
              : isInRangeFilter || getFilterType(fieldType) === 'date'
              ? filterFrom
              : filterSingle,
          }
        : {}),

      type: filterOperatorType,
      ...(isInRangeFilter ? { filterTo } : {}),
    };

    if (isPreviewMode && filterModel._cnf_.length >= 1) {
      return showPreviewModeUpsellModal();
    }
    dispatch(setIsCustomColumnHeaderContextMenuOpen(false));

    applyFilterModelCnfFromQuickFilters(quickFilterModel);
    dispatch(
      addMixpanelEvent({
        eventName: 'Quick Filter Used',
        eventProps: {},
        userIncrementName: '# of Quick Filter Used',
      })
    );
  };

  useEffect(() => {
    const rule = analyzeFilterRules(filterModel?._cnf_, colId);
    setComponentMapRule(rule);
  }, []);

  useEffect(() => {
    if (componentMapRule !== COMPONENT_RULES.QUICK_FILTER) return;
    const flatFilters = getCurrentAppliedFilters(filterModel?._cnf_).filter(
      (el) => el.colId === colId
    );
    setFilterOperatorType(
      !flatFilters.length ? operatorOptions[1].value || '' : flatFilters[0].type
    );
  }, [colId, componentMapRule]);

  useEffect(() => {
    if (componentMapRule !== COMPONENT_RULES.QUICK_FILTER) return;
    const flatFilters = getCurrentAppliedFilters(filterModel?._cnf_).filter(
      (el) => el.colId === colId
    );
    if (!flatFilters?.length) return;

    if (isFilterEmpty) return;
    const filter = flatFilters[0]?.filter || '';
    const filterTo = flatFilters[0]?.filterTo || '';
    isInputWithSuggestions
      ? setFilterMultiple(filter)
      : isInRangeFilter || getFilterType(fieldType) === 'date'
      ? setFilterFrom(filter)
      : setFilterSingle(() => filter);
    if (filterTo) setFilterTo(filterTo);
  }, [isInputWithSuggestions, filterModel?._cnf_, componentMapRule, colId]);

  const handleOperatorType = (v) => {
    if (v === 'none') {
      setFilterTo('');
      setFilterFrom('');
      setFilterSingle('');
      setFilterMultiple([]);
      setFilterOperatorType(v);
      return;
    } else {
      setFilterOperatorType(v);
    }
  };
  return (
    <>
      <hr className='w-full my-2 border-ui-200' />
      <div data-cy='qf-main-wrapper' className='bg-ui w-[250px] p-2 pt-0'>
        {componentMapRule === COMPONENT_RULES.OR && (
          <UnableFilters handleAdvanced={handleAdvanced} />
        )}
        {componentMapRule === COMPONENT_RULES.MULTIPLE && (
          <UnableFilters
            handleAdvanced={handleAdvanced}
            text='Multiple rules set; filter can only be applied using advanced controls.'
          />
        )}
        {componentMapRule === COMPONENT_RULES.QUICK_FILTER && (
          <>
            <QuickFiltersSelect
              size='small'
              placeholder='Select Operator'
              options={operatorOptions}
              value={filterOperatorType}
              onChange={handleOperatorType}
              color='oceanBlueLight'
              buttonClassName='active:border-ocean-blue-400 focus:border-ocean-blue-500 focus:ring-2 focus:ring-midnight-100 mb-2'
              dataCy='qf-select-operator'
            />
            {fieldType === 'Boolean' && !isBlankConditions && !isFilterEmpty && (
              <QuickFiltersSelect
                size='small'
                placeholder='Value'
                options={booleanOptions}
                value={filterSingle}
                onChange={(v) => {
                  setFilterSingle(v);
                }}
                color='oceanBlueLight'
                buttonClassName='active:border-ocean-blue-400 focus:border-ocean-blue-500 focus:ring-2 focus:ring-midnight-100 mb-3 !h-6 text-xs'
                dataCy='operator-type-selector'
              />
            )}
            {/* BOOLEAN SELECT */}
            {fieldType !== 'Boolean' &&
              fieldType !== 'DateTime' &&
              fieldType !== 'DateTime64' &&
              fieldType !== 'metadata_tag' &&
              !isInRangeFilter &&
              !isFilterEmpty &&
              !isBlankConditions && (
                <>
                  {isInputWithSuggestions ? (
                    <>
                      <div className='relative'>
                        <Icon
                          size={16}
                          name='magnifying-glass'
                          className='absolute text-gray-500 transform -translate-y-1/2 left-2 top-1/2'
                        />
                        <TextInput
                          placeholder='Search'
                          className='w-full text-xs !h-8 pl-7 active:border-ocean-blue-400 focus:border-ocean-blue-500 focus:ring-2 focus:ring-midnight-100'
                          value={searchQFValueQuery}
                          onChange={(e) => setSearchQFValueQuery(e.target.value)}
                          dataCy='qf-filter-search'
                        />
                      </div>
                      <OptionList
                        searchQuery={searchQFValueQuery}
                        multipleValue={filterMultiple}
                        setMultipleValue={setFilterMultiple}
                        colId={colId}
                        fieldType={fieldType}
                        filterOperatorType={filterOperatorType}
                      />
                    </>
                  ) : (
                    <TextInput
                      dataCy='qf-single-value'
                      placeholder='Value'
                      className='w-full h-6 text-xs'
                      value={filterSingle}
                      onChange={(e) => setFilterSingle(e.target.value)}
                    />
                  )}
                </>
              )}
            {getFilterType(fieldType) === 'date' && !isBlankConditions && !isFilterEmpty && (
              <div className='quick-filter-calendar'>
                <Calendar
                  onChange={handleDateChange}
                  defaultStartValue={filterFrom || undefined}
                  defaultEndValue={filterTo || undefined}
                  isMobile={true}
                  modelType={filterOperatorType}
                  portalId='body'
                  id={1}
                />
              </div>
            )}
            {isInRangeFilter &&
              fieldType !== 'DateTime' &&
              fieldType !== 'DateTime64' &&
              !isFilterEmpty &&
              !isBlankConditions && (
                <>
                  <TextInput
                    dataCy='qf-value-from'
                    placeholder='Value From'
                    className='w-full h-6 text-xs'
                    value={filterFrom}
                    onChange={(e) => setFilterFrom(e.target.value)}
                  />
                  <TextInput
                    dataCy='qf-value-to'
                    placeholder='Value To'
                    className='w-full h-6 my-3 text-xs'
                    value={filterTo}
                    onChange={(e) => setFilterTo(e.target.value)}
                  />
                </>
              )}
            <div className='flex flex-row items-center justify-between mt-2 text-right'>
              <div>
                <Tooltip text='Advanced Filters' side='bottom'>
                  <Button
                    onClick={handleAdvanced}
                    className='px-2'
                    size='small'
                    variant='ghost'
                    iconName='gear'
                    dataCy='qf-advanced'
                  />
                </Tooltip>
              </div>
              <div className='flex gap-1'>
                <Button
                  className='pt-[2px]'
                  size='small'
                  variant='ghost'
                  color='shadow'
                  dataCy='cancel-btn'
                  onClick={handleCancel}
                >
                  Cancel
                </Button>
                <Button
                  size='small'
                  color={'oceanBlue'}
                  className='shrink-0 pt-[2px]'
                  dataCy='save-btn'
                  onClick={handleApply}
                  disabled={getIsApplyDisabled()}
                >
                  Apply
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
};
